import React, { useState } from 'react';
import { Tag, Plus, X, Check } from 'lucide-react';
import { useTaskContext } from '../../contexts/TaskContext';
import type { TaskLabel } from '../../types';

interface LabelSelectorProps {
  selectedLabels: TaskLabel[];
  onLabelsChange: (labels: TaskLabel[]) => void;
}

export function LabelSelector({ selectedLabels, onLabelsChange }: LabelSelectorProps) {
  const { labels, addLabel, deleteLabel } = useTaskContext();
  const [isOpen, setIsOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#FF5733');

  const handleAddLabel = (e: React.FormEvent) => {
    if (newLabelName.trim()) {
      const newLabel = {
        name: newLabelName.trim(),
        color: newLabelColor,
      };
      addLabel(newLabel);
      setNewLabelName('');
      setNewLabelColor('#FF5733');
      setShowAddForm(false);
    }
  };

  const handleDeleteLabel = (labelId: string) => {
    if (confirm('Are you sure you want to delete this label? It will be removed from all tasks.')) {
      deleteLabel(labelId);
      // Remove the deleted label from selected labels
      onLabelsChange(selectedLabels.filter(label => label.id !== labelId));
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-black/20 rounded-xl hover:bg-black/30 transition-colors"
      >
        <Tag className="w-4 h-4" />
        <span>
          {selectedLabels.length
            ? `${selectedLabels.length} label${selectedLabels.length === 1 ? '' : 's'}`
            : 'Add labels'}
        </span>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 w-64 bg-[#2A2B2E] rounded-xl p-4 shadow-lg z-10">
          <div className="space-y-4">
            {!showAddForm ? (
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 w-full px-3 py-2 bg-black/20 rounded-lg hover:bg-black/30 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Create new label
              </button>
            ) : (
              <div className="space-y-3">
                <div>
                  <input
                    type="text"
                    value={newLabelName}
                    onChange={(e) => setNewLabelName(e.target.value)}
                    placeholder="Label name"
                    className="w-full px-3 py-2 bg-black/20 rounded-lg text-sm placeholder-gray-400"
                    autoFocus
                  />
                </div>
                <div>
                  <input
                    type="color"
                    value={newLabelColor}
                    onChange={(e) => setNewLabelColor(e.target.value)}
                    className="w-full h-8 rounded-lg cursor-pointer"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    onClick={handleAddLabel}
                    className="flex items-center gap-2 px-3 py-2 bg-[var(--accent-color)] rounded-lg text-black text-sm hover:opacity-90 transition-opacity"
                  >
                    <Check className="w-4 h-4" />
                    Add Label
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="flex items-center gap-2 px-3 py-2 bg-black/20 rounded-lg text-sm hover:bg-black/30 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {labels.map((label) => (
              <label key={label.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedLabels.some(l => l.id === label.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onLabelsChange([...selectedLabels, label]);
                    } else {
                      onLabelsChange(selectedLabels.filter(l => l.id !== label.id));
                    }
                  }}
                  className="rounded bg-black/20"
                />
                <span className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: label.color }}
                  />
                  {label.name}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeleteLabel(label.id);
                  }}
                  className="ml-auto p-1 hover:bg-black/20 rounded-lg text-red-500 hover:text-red-400 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}